import { Language } from "@linqx/i18n";
import { LegalLayout } from "@/components/sections/legal-layout";

export function PrivacyContent() {
  return (
    <LegalLayout
      title={Language.getKey("landing_legal_privacy_title")}
      lastUpdated={Language.getKey("landing_legal_privacy_updated")}
    >
      <section className="mb-8">
        <p className="text-muted-foreground mb-4">
          This Privacy Notice for Linqx (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;), describes how and why we might access, collect,
          store, use, and/or share (&ldquo;process&rdquo;) your personal
          information when you use our services (&ldquo;Services&rdquo;),
          including when you:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>
            Visit our website at getlinqx.com, or any website of ours that links
            to this Privacy Notice
          </li>
          <li>
            Download and use our mobile application (Linqx), or any other
            application of ours that links to this Privacy Notice
          </li>
          <li>
            Engage with us in other related ways, including any sales,
            marketing, or events
          </li>
        </ul>
        <p className="text-muted-foreground mb-4">
          <strong>Questions or concerns?</strong> Reading this Privacy Notice
          will help you understand your privacy rights and choices. If you do
          not agree with our policies and practices, please do not use our
          Services. If you still have any questions or concerns, please contact
          us at{" "}
          <a
            href="mailto:support@getlinqx.com"
            className="text-primary hover:underline"
          >
            support@getlinqx.com
          </a>
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          Summary of Key Points
        </h2>
        <p className="text-muted-foreground mb-4">
          This summary provides key points from our Privacy Notice. You can find
          out more details about any of these topics by using our table of
          contents below.
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>What personal information do we process?</strong> We process
            personal information depending on how you interact with us and the
            Services, the choices you make, and the products and features you
            use.
          </li>
          <li>
            <strong>Do we process any sensitive personal information?</strong>{" "}
            Some of the information may be considered &ldquo;special&rdquo; or
            &ldquo;sensitive&rdquo; in certain jurisdictions, for example your
            racial or ethnic origins and information about your sex life or
            sexual orientation. We only process sensitive personal information
            when necessary and with your consent.
          </li>
          <li>
            <strong>Do we collect any information from third parties?</strong>{" "}
            We may collect information from public databases, marketing
            partners, social media platforms, and other outside sources.
          </li>
          <li>
            <strong>How do we process your information?</strong> We process your
            information to provide, improve, and administer our Services,
            communicate with you, for security and fraud prevention, and to
            comply with law.
          </li>
          <li>
            <strong>How do we keep your information safe?</strong> We have
            adequate organizational and technical processes and procedures in
            place to protect your personal information. However, no electronic
            transmission over the internet or information storage technology can
            be guaranteed to be 100% secure.
          </li>
          <li>
            <strong>What are your rights?</strong> Depending on where you are
            located geographically, the applicable privacy law may mean you have
            certain rights regarding your personal information.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          Table of Contents
        </h2>
        <ol className="list-decimal list-inside text-muted-foreground space-y-1">
          <li>
            <a href="#section-1" className="text-primary hover:underline">
              What Information Do We Collect?
            </a>
          </li>
          <li>
            <a href="#section-2" className="text-primary hover:underline">
              How Do We Process Your Information?
            </a>
          </li>
          <li>
            <a href="#section-3" className="text-primary hover:underline">
              What Legal Bases Do We Rely On To Process Your Personal
              Information?
            </a>
          </li>
          <li>
            <a href="#section-4" className="text-primary hover:underline">
              When And With Whom Do We Share Your Personal Information?
            </a>
          </li>
          <li>
            <a href="#section-5" className="text-primary hover:underline">
              What Is Our Stance On Third-Party Websites?
            </a>
          </li>
          <li>
            <a href="#section-6" className="text-primary hover:underline">
              Do We Use Cookies And Other Tracking Technologies?
            </a>
          </li>
          <li>
            <a href="#section-7" className="text-primary hover:underline">
              Do We Offer Artificial Intelligence-Based Products?
            </a>
          </li>
          <li>
            <a href="#section-8" className="text-primary hover:underline">
              How Do We Handle Your Social Logins?
            </a>
          </li>
          <li>
            <a href="#section-9" className="text-primary hover:underline">
              How Long Do We Keep Your Information?
            </a>
          </li>
          <li>
            <a href="#section-10" className="text-primary hover:underline">
              How Do We Keep Your Information Safe?
            </a>
          </li>
          <li>
            <a href="#section-11" className="text-primary hover:underline">
              Do We Collect Information From Minors?
            </a>
          </li>
          <li>
            <a href="#section-12" className="text-primary hover:underline">
              What Are Your Privacy Rights?
            </a>
          </li>
          <li>
            <a href="#section-13" className="text-primary hover:underline">
              Controls For Do-Not-Track Features
            </a>
          </li>
          <li>
            <a href="#section-14" className="text-primary hover:underline">
              Do United States Residents Have Specific Privacy Rights?
            </a>
          </li>
          <li>
            <a href="#section-15" className="text-primary hover:underline">
              Do Other Regions Have Specific Privacy Rights?
            </a>
          </li>
          <li>
            <a href="#section-16" className="text-primary hover:underline">
              Data Sharing With Core API
            </a>
          </li>
          <li>
            <a href="#section-17" className="text-primary hover:underline">
              Do We Make Updates To This Notice?
            </a>
          </li>
          <li>
            <a href="#section-18" className="text-primary hover:underline">
              How Can You Contact Us About This Notice?
            </a>
          </li>
          <li>
            <a href="#section-19" className="text-primary hover:underline">
              How Can You Review, Update, Or Delete The Data We Collect From
              You?
            </a>
          </li>
        </ol>
      </section>

      <section id="section-1" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          1. What Information Do We Collect?
        </h2>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          Personal information you disclose to us
        </h3>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We collect personal information that you provide to us.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us.
        </p>
        <p className="text-muted-foreground mb-4">
          <strong>Personal Information Provided by You.</strong> The personal
          information that we collect depends on the context of your
          interactions with us and the Services, the choices you make, and the
          products and features you use. The personal information we collect may
          include the following:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>Names</li>
          <li>Phone numbers</li>
          <li>Email addresses</li>
          <li>Mailing addresses</li>
          <li>Job titles</li>
          <li>Usernames</li>
          <li>Passwords</li>
          <li>Contact preferences</li>
          <li>Contact or authentication data</li>
          <li>Billing addresses</li>
          <li>Debit/credit card numbers</li>
        </ul>

        <p className="text-muted-foreground mb-4">
          <strong>Sensitive Information.</strong> When necessary, with your
          consent or as otherwise permitted by applicable law, we process the
          following categories of sensitive information:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>Data about a person&apos;s sex life or sexual orientation</li>
          <li>Information revealing race or ethnic origin</li>
        </ul>

        <p className="text-muted-foreground mb-4">
          <strong>Payment Data.</strong> We may collect data necessary to
          process your payment if you choose to make purchases, such as your
          payment instrument number, and the security code associated with your
          payment instrument. All payment data is handled and stored by Stripe.
          You may find their privacy notice link(s) here:{" "}
          <a
            href="https://stripe.com/privacy"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://stripe.com/privacy
          </a>
        </p>

        <p className="text-muted-foreground mb-4">
          <strong>Social Media Login Data.</strong> We may provide you with the
          option to register with us using your existing social media account
          details, like your Facebook, X, or other social media account.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          Information automatically collected
        </h3>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: Some information — such as your Internet Protocol (IP)
            address and/or browser and device characteristics — is collected
            automatically when you visit our Services.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          We automatically collect certain information when you visit, use, or
          navigate the Services. This information does not reveal your specific
          identity (like your name or contact information) but may include
          device and usage information, such as your IP address, browser and
          device characteristics, operating system, language preferences,
          referring URLs, device name, country, location, information about how
          and when you use our Services, and other technical information. This
          information is primarily needed to maintain the security and operation
          of our Services, and for our internal analytics and reporting
          purposes.
        </p>
        <p className="text-muted-foreground mb-4">
          The information we collect includes:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>
            <strong>Log and Usage Data.</strong> Log and usage data is
            service-related, diagnostic, usage, and performance information our
            servers automatically collect when you access or use our Services
            and which we record in log files.
          </li>
          <li>
            <strong>Device Data.</strong> We collect device data such as
            information about your computer, phone, tablet, or other device you
            use to access the Services.
          </li>
          <li>
            <strong>Location Data.</strong> We collect location data such as
            information about your device&apos;s location, which can be either
            precise or imprecise.
          </li>
        </ul>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          Information collected through our App
        </h3>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We collect information regarding your geolocation, mobile
            device, push notifications, when you use our App.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          If you use our App, we also collect the following information:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>
            <strong>Geolocation Information.</strong> We may request access or
            permission to track location-based information from your mobile
            device, either continuously or while you are using our App, to
            provide certain location-based services.
          </li>
          <li>
            <strong>Mobile Device Access.</strong> We may request access or
            permission to certain features from your mobile device, including
            your mobile device&apos;s calendar, camera, contacts, SMS messages,
            and other features.
          </li>
          <li>
            <strong>Push Notifications.</strong> We may request to send you push
            notifications regarding your account or certain features of the App.
          </li>
        </ul>

        <h3 className="text-xl font-medium mb-3 text-foreground">Google API</h3>
        <p className="text-muted-foreground">
          Our use of information received from Google APIs will adhere to Google
          API Services User Data Policy, including the Limited Use requirements.
        </p>
      </section>

      <section id="section-2" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          2. How Do We Process Your Information?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We process your information to provide, improve, and
            administer our Services, communicate with you, for security and
            fraud prevention, and to comply with law.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            To facilitate account creation and authentication and otherwise
            manage user accounts
          </li>
          <li>To deliver and facilitate delivery of services to the user</li>
          <li>To respond to user inquiries/offer support to users</li>
          <li>To send administrative information to you</li>
          <li>To fulfill and manage your orders</li>
          <li>To enable user-to-user communications</li>
          <li>To request feedback</li>
          <li>To send you marketing and promotional communications</li>
          <li>To deliver targeted advertising to you</li>
          <li>To protect our Services</li>
          <li>To identify usage trends</li>
          <li>
            To determine the effectiveness of our marketing and promotional
            campaigns
          </li>
          <li>To save or protect an individual&apos;s vital interest</li>
        </ul>
      </section>

      <section id="section-3" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          3. What Legal Bases Do We Rely On To Process Your Information?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We only process your personal information when we believe
            it is necessary and we have a valid legal reason to do so under
            applicable law.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. We may rely on the following legal bases:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>Consent.</strong> We may process your information if you
            have given us permission to use your personal information for a
            specific purpose.
          </li>
          <li>
            <strong>Performance of a Contract.</strong> We may process your
            personal information when we believe it is necessary to fulfill our
            contractual obligations to you.
          </li>
          <li>
            <strong>Legitimate Interests.</strong> We may process your
            information when we believe it is reasonably necessary to achieve
            our legitimate business interests.
          </li>
          <li>
            <strong>Legal Obligations.</strong> We may process your information
            where we believe it is necessary for compliance with our legal
            obligations.
          </li>
          <li>
            <strong>Vital Interests.</strong> We may process your information
            where we believe it is necessary to protect your vital interests or
            the vital interests of a third party.
          </li>
        </ul>
      </section>

      <section id="section-4" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          4. When And With Whom Do We Share Your Personal Information?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We may share information in specific situations described
            in this section and/or with the following categories of third
            parties.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          <strong>
            Vendors, Consultants, and Other Third-Party Service Providers.
          </strong>{" "}
          We may share your data with third-party vendors, service providers,
          contractors, or agents (&ldquo;third parties&rdquo;) who perform
          services for us or on our behalf and require access to such
          information to do that work. The categories of third parties we may
          share personal information with are as follows:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>Ad Networks</li>
          <li>Affiliate Marketing Programs</li>
          <li>AI Platforms</li>
          <li>Communication &amp; Collaboration Tools</li>
          <li>Cloud Computing Services</li>
          <li>Data Analytics Services</li>
          <li>Data Storage Service Providers</li>
          <li>Finance &amp; Accounting Tools</li>
          <li>Payment Processors</li>
          <li>Performance Monitoring Tools</li>
          <li>Product Engineering &amp; Design Tools</li>
          <li>Retargeting Platforms</li>
          <li>Sales &amp; Marketing Tools</li>
          <li>Social Networks</li>
          <li>Testing Tools</li>
          <li>User Account Registration &amp; Authentication Services</li>
          <li>Website Hosting Service Providers</li>
        </ul>
        <p className="text-muted-foreground mb-4">
          We also may need to share your personal information in the following
          situations:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>Business Transfers.</strong> We may share or transfer your
            information in connection with, or during negotiations of, any
            merger, sale of company assets, financing, or acquisition of all or
            a portion of our business to another company.
          </li>
          <li>
            <strong>Business Partners.</strong> We may share your information
            with our business partners to offer you certain products, services,
            or promotions.
          </li>
          <li>
            <strong>Other Users.</strong> When you share personal information or
            otherwise interact in the public areas with other users, such
            information may be viewed by all users and may be publicly made
            available outside the Services in perpetuity.
          </li>
        </ul>
      </section>

      <section id="section-5" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          5. What Is Our Stance On Third-Party Websites?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We are not responsible for the safety of any information
            that you share with third parties that we may link to or who
            advertise on our Services, but are not affiliated with, our
            Services.
          </em>
        </p>
        <p className="text-muted-foreground">
          The Services, including our offer wall, may link to third-party
          websites, online services, or mobile applications and/or contain
          advertisements from third parties that are not affiliated with us and
          which may link to other websites, services, or applications.
          Accordingly, we do not make any guarantee regarding any such third
          parties, and we will not be liable for any loss or damage caused by
          the use of such third-party websites, services, or applications.
        </p>
      </section>

      <section id="section-6" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          6. Do We Use Cookies And Other Tracking Technologies?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We may use cookies and other tracking technologies to
            collect and store your information.
          </em>
        </p>
        <p className="text-muted-foreground">
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to gather information when you interact with our Services.
          Some online tracking technologies help us maintain the security of our
          Services and your account, prevent crashes, fix bugs, save your
          preferences, and assist with basic site functions.
        </p>
      </section>

      <section id="section-7" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          7. Do We Offer Artificial Intelligence-Based Products?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We offer products, features, or tools powered by
            artificial intelligence, machine learning, or similar technologies.
          </em>
        </p>
        <p className="text-muted-foreground">
          As part of our Services, we offer products, features, or tools powered
          by artificial intelligence, machine learning, or similar technologies
          (collectively, &ldquo;AI Products&rdquo;). These tools are designed to
          enhance your experience and provide you with innovative solutions
          tailored to your needs.
        </p>
      </section>

      <section id="section-8" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          8. How Do We Handle Your Social Logins?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: If you choose to register or log in to our Services using
            a social media account, we may have access to certain information
            about you.
          </em>
        </p>
        <p className="text-muted-foreground">
          Our Services offer you the ability to register and log in using your
          third-party social media account details (like your Facebook or X
          logins). Where you choose to do this, we will receive certain profile
          information about you from your social media provider.
        </p>
      </section>

      <section id="section-9" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          9. How Long Do We Keep Your Information?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We keep your information for as long as necessary to
            fulfill the purposes outlined in this Privacy Notice unless
            otherwise required by law.
          </em>
        </p>
        <p className="text-muted-foreground">
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this Privacy Notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements).
        </p>
      </section>

      <section id="section-10" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          10. How Do We Keep Your Information Safe?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We aim to protect your personal information through a
            system of organizational and technical security measures.
          </em>
        </p>
        <p className="text-muted-foreground">
          We have implemented appropriate and reasonable technical and
          organizational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information.
        </p>
      </section>

      <section id="section-11" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          11. Do We Collect Information From Minors?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: We do not knowingly collect data from or market to
            children under 18 years of age.
          </em>
        </p>
        <p className="text-muted-foreground">
          We do not knowingly collect, solicit data from, or market to children
          under 18 years of age, nor do we knowingly sell such personal
          information. By using the Services, you represent that you are at
          least 18 or that you are the parent or guardian of such a minor and
          consent to such minor dependent&apos;s use of the Services.
        </p>
      </section>

      <section id="section-12" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          12. What Are Your Privacy Rights?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: Depending on your state of residence in the US or in some
            regions, such as the European Economic Area (EEA), United Kingdom
            (UK), Switzerland, and Canada, you have rights that allow you
            greater access to and control over your personal information.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          In some regions (like the EEA, UK, Switzerland, and Canada), you have
          certain rights under applicable data protection laws. These may
          include the right (i) to request access and obtain a copy of your
          personal information, (ii) to request rectification or erasure; (iii)
          to restrict the processing of your personal information; (iv) if
          applicable, to data portability; and (v) not to be subject to
          automated decision-making.
        </p>
        <p className="text-muted-foreground mb-4">
          <strong>Withdrawing your consent:</strong> If we are relying on your
          consent to process your personal information, you have the right to
          withdraw your consent at any time.
        </p>
        <p className="text-muted-foreground">
          <strong>Account Information:</strong> If you would at any time like to
          review or change the information in your account or terminate your
          account, you can log in to your account settings and update your user
          account, or contact us at{" "}
          <a
            href="mailto:support@getlinqx.com"
            className="text-primary hover:underline"
          >
            support@getlinqx.com
          </a>
        </p>
      </section>

      <section id="section-13" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          13. Controls For Do-Not-Track Features
        </h2>
        <p className="text-muted-foreground">
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track (&ldquo;DNT&rdquo;) feature or
          setting you can activate to signal your privacy preference not to have
          data about your online browsing activities monitored and collected. At
          this stage, no uniform technology standard for recognizing and
          implementing DNT signals has been finalized.
        </p>
      </section>

      <section id="section-14" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          14. Do United States Residents Have Specific Privacy Rights?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: If you are a resident of California, Colorado,
            Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Montana,
            New Hampshire, New Jersey, Oregon, Tennessee, Texas, Utah, or
            Virginia, you may have the right to request access to and receive
            details about the personal information we maintain about you and how
            we have processed it, correct inaccuracies, get a copy of, or delete
            your personal information.
          </em>
        </p>
        <p className="text-muted-foreground">
          We have collected the following categories of personal information in
          the past twelve (12) months. The specific personal information we
          collected includes identifiers, personal information as defined in the
          California Customer Records statute, protected classification
          characteristics under California or federal law, commercial
          information, biometric information, internet or other similar network
          activity, geolocation data,
          audio/electronic/visual/thermal/olfactory/similar information,
          professional or employment-related information, education information,
          and inferences drawn from the collected personal information.
        </p>
      </section>

      <section id="section-15" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          15. Do Other Regions Have Specific Privacy Rights?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: You may have additional rights based on the country you
            reside in.
          </em>
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          Australia and New Zealand
        </h3>
        <p className="text-muted-foreground mb-4">
          We collect and process your personal information under the obligations
          and conditions set by Australia&apos;s Privacy Act 1988 and New
          Zealand&apos;s Privacy Act 2020.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          Republic of South Africa
        </h3>
        <p className="text-muted-foreground">
          We collect and process your personal information under the Protection
          of Personal Information Act (&ldquo;POPIA&rdquo;).
        </p>
      </section>

      <section id="section-16" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          16. Data Sharing With Core API
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            <strong>In Short:</strong> We may share your data through
            Google&apos;s Core API services in compliance with their usage
            policies.
          </em>
        </p>
        <p className="text-muted-foreground mb-4">
          Our use of information received from Google APIs will adhere to{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. This means that we only use
          and transfer information received from Google APIs to other apps in
          compliance with these policies.
        </p>
        <p className="text-muted-foreground mb-4">
          We may share data accessed through Google&apos;s Core API with third
          parties only in the following circumstances:
        </p>
        <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
          <li>With your explicit consent</li>
          <li>When necessary to provide a feature you requested</li>
          <li>
            For external processing by our service providers who are bound by
            appropriate confidentiality obligations
          </li>
          <li>When required by law or to protect our rights</li>
        </ul>
        <p className="text-muted-foreground">
          We do not use data obtained through Google&apos;s Core API for
          advertising purposes or to create advertising profiles.
        </p>
      </section>

      <section id="section-17" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          17. Do We Make Updates To This Notice?
        </h2>
        <p className="text-muted-foreground mb-4">
          <em>
            In Short: Yes, we will update this notice as necessary to stay
            compliant with relevant laws.
          </em>
        </p>
        <p className="text-muted-foreground">
          We may update this Privacy Notice from time to time. The updated
          version will be indicated by an updated &ldquo;Revised&rdquo; date at
          the top of this Privacy Notice. If we make material changes to this
          Privacy Notice, we may notify you either by prominently posting a
          notice of such changes or by directly sending you a notification.
        </p>
      </section>

      <section id="section-18" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          18. How Can You Contact Us About This Notice?
        </h2>
        <p className="text-muted-foreground">
          If you have questions or comments about this notice, you may email us
          at{" "}
          <a
            href="mailto:support@getlinqx.com"
            className="text-primary hover:underline"
          >
            support@getlinqx.com
          </a>
        </p>
      </section>

      <section id="section-19" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          19. How Can You Review, Update, Or Delete The Data We Collect From
          You?
        </h2>
        <p className="text-muted-foreground">
          Based on the applicable laws of your country or state of residence in
          the US, you may have the right to request access to the personal
          information we collect from you, details about how we have processed
          it, correct inaccuracies, or delete your personal information. You may
          also have the right to withdraw your consent to our processing of your
          personal information. To request to review, update, or delete your
          personal information, please visit:{" "}
          <a
            href="https://getlinqx.com/settings"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            getlinqx.com/settings
          </a>
        </p>
      </section>
    </LegalLayout>
  );
}
